/**
 * Byte-mode (ISO/IEC 18004 §7.4.5) data encoding: mode/character-count
 * headers, version selection, terminator/pad-byte filling, Reed–Solomon
 * block splitting and interleaving, and the version's trailing remainder
 * bits (§7.4.10) — the pipeline that turns a UTF-8 payload into the final
 * bit sequence `matrix.ts` places into the module grid.
 */
import { rsGenerateEccCodewords } from './galois-field'
import { ECC_BLOCK_TABLE, REMAINDER_BITS } from './tables'
import type { QrErrorCorrectionLevel } from './types'

const BYTE_MODE_INDICATOR = 0b0100

/** Byte-mode character-count indicator width: 8 bits for versions 1-9, 16 for versions 10-40. */
function characterCountBits(version: number): number {
	return version < 10 ? 8 : 16
}

/** Appends `value`'s low `length` bits, most-significant first. */
function pushBits(bits: number[], value: number, length: number): void {
	for (let i = length - 1; i >= 0; i--) bits.push((value >> i) & 1)
}

function bitsToBytes(bits: readonly number[]): number[] {
	const bytes: number[] = []
	for (let i = 0; i < bits.length; i += 8) {
		let byte = 0
		for (let j = 0; j < 8; j++) byte = (byte << 1) | bits[i + j]
		bytes.push(byte)
	}
	return bytes
}

/**
 * The smallest version (1-40) whose data capacity, at `level`, fits the
 * byte-mode header plus `dataByteLength` bytes of payload.
 */
export function chooseVersion(dataByteLength: number, level: QrErrorCorrectionLevel): number {
	for (let version = 1; version <= 40; version++) {
		const info = ECC_BLOCK_TABLE[version]?.[level]
		if (!info) continue
		const capacityBits = info.totalDataCodewords * 8
		const headerBits = 4 + characterCountBits(version)
		if (headerBits + dataByteLength * 8 <= capacityBits) return version
	}
	throw new Error(
		`qrToSvg: payload too large for QR Code Model 2 (max 2953 bytes at level L) — ${dataByteLength} bytes at level ${level}`,
	)
}

/**
 * Builds the full data-codeword sequence for one symbol: mode indicator +
 * character-count indicator + the payload bytes, terminator, byte-boundary
 * padding, then the `11101100`/`00010001` pad-byte cycle (§7.4.9) until the
 * version's data capacity is exactly filled.
 */
export function buildDataCodewords(
	dataBytes: readonly number[],
	version: number,
	level: QrErrorCorrectionLevel,
): number[] {
	const info = ECC_BLOCK_TABLE[version]?.[level]
	if (!info) throw new Error(`qrToSvg: no ECC table entry for version ${version} level ${level}`)
	const capacityBits = info.totalDataCodewords * 8

	const bits: number[] = []
	pushBits(bits, BYTE_MODE_INDICATOR, 4)
	pushBits(bits, dataBytes.length, characterCountBits(version))
	for (const byte of dataBytes) pushBits(bits, byte, 8)

	const terminatorLength = Math.max(0, Math.min(4, capacityBits - bits.length))
	for (let i = 0; i < terminatorLength; i++) bits.push(0)
	while (bits.length % 8 !== 0) bits.push(0)

	const padBytes = [0b11101100, 0b00010001]
	let padIndex = 0
	while (bits.length < capacityBits) {
		pushBits(bits, padBytes[padIndex % 2], 8)
		padIndex++
	}
	return bitsToBytes(bits)
}

/** Splits `dataCodewords` into the version's group-1-then-group-2 blocks (§7.5.2). */
function splitIntoBlocks(
	dataCodewords: readonly number[],
	group1Blocks: number,
	group1Size: number,
	group2Blocks: number,
	group2Size: number,
): number[][] {
	const blocks: number[][] = []
	let offset = 0
	for (let i = 0; i < group1Blocks; i++) {
		blocks.push(dataCodewords.slice(offset, offset + group1Size))
		offset += group1Size
	}
	for (let i = 0; i < group2Blocks; i++) {
		blocks.push(dataCodewords.slice(offset, offset + group2Size))
		offset += group2Size
	}
	return blocks
}

/** Column-major interleave: codeword 1 of every block, then codeword 2 of every block, and so on (§7.5.3). */
function interleaveBlocks(blocks: readonly (readonly number[])[]): number[] {
	const maxLength = Math.max(...blocks.map(block => block.length))
	const interleaved: number[] = []
	for (let column = 0; column < maxLength; column++) {
		for (const block of blocks) {
			if (column < block.length) interleaved.push(block[column])
		}
	}
	return interleaved
}

/**
 * The final bit sequence `matrix.ts` places into the module grid:
 * interleaved data codewords, then interleaved Reed–Solomon codewords
 * (computed per block, per §7.5), then the version's trailing remainder
 * bits (§7.4.10) — all as a flat `0`/`1` array, MSB-first per byte.
 */
export function buildFinalBitStream(
	dataCodewords: readonly number[],
	version: number,
	level: QrErrorCorrectionLevel,
): number[] {
	const info = ECC_BLOCK_TABLE[version]?.[level]
	if (!info) throw new Error(`qrToSvg: no ECC table entry for version ${version} level ${level}`)

	const dataBlocks = splitIntoBlocks(
		dataCodewords,
		info.group1Blocks,
		info.group1DataCodewords,
		info.group2Blocks,
		info.group2DataCodewords,
	)
	const eccBlocks = dataBlocks.map(block => rsGenerateEccCodewords(block, info.ecCodewordsPerBlock))

	const finalCodewords = [...interleaveBlocks(dataBlocks), ...interleaveBlocks(eccBlocks)]

	const bits: number[] = []
	for (const byte of finalCodewords) pushBits(bits, byte, 8)
	const remainder = REMAINDER_BITS[version] ?? 0
	for (let i = 0; i < remainder; i++) bits.push(0)
	return bits
}
